import {MaterialReference} from "@cm/template-nodes"
import type {IModifyTemplateGraph} from "@cm/template-nodes"
import type {
    InitStackableMattressStandardModelMasterTemplateOptions,
    InitMattressInstanceTemplateOptions,
} from "#mattress/assets-definitions/standard-models/common/model-factory"
import {
    initStackableMattressStandardModelMasterTemplate,
    initInstanceTemplateCommon,
    getDefaultHandlesParameters,
} from "#mattress/assets-definitions/standard-models/common/model-factory"
import {createTemplateReference} from "#mattress/assets-definitions/common"

export function initEdgeMasterTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    options: InitStackableMattressStandardModelMasterTemplateOptions = {openingOnBottom: false},
) {
    initStackableMattressStandardModelMasterTemplate(modifyTemplateGraph, {
        ...options,
        topPartFabricLayout: "Waterfall",
        bottomPartFabricLayout: "Border",
        middlePartFabricLayout: "Border",
        middlePartDefaultMaterialLegacyId: options.topPartDefaultMaterialLegacyId,
    })
}

function tapeForLayerTopCurveAccessories(options: InitMattressInstanceTemplateOptions, layerIdx: number) {
    return {
        [`fabric-stack/layer-${layerIdx}/accessories-layer-top-curve/accessory-count`]: 1,
        [`fabric-stack/layer-${layerIdx}/accessories-layer-top-curve/accessory-stack/accessory-0/accessory`]: createTemplateReference(
            options.accessoriesTemplatesLegacyIds["tape 8mm"],
        ),
    }
}

const defaultDimensions = {
    width: 90,
    length: 200,
    "top-height": 1,
    "bottom-height": 1,
    "corner-radius": 5,
    "top-radius": 2,
    "bottom-radius": 2,
}

export function initEdgeInstanceTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    masterTemplateDataLegacyId: number,
    options: InitMattressInstanceTemplateOptions,
) {
    const optionsParameters = {
        ...(options.parameters ? options.parameters : {}),
        ...defaultDimensions,
        "layer-count": 1,
        "fabric-stack/layer-0/height": 20,
        "accessories-bottom-curve/accessory-count": 2,
        "accessories-bottom-curve/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["zipper Teeth, M5"]),
        "accessories-bottom-curve/accessory-stack/accessory-0/accessory/puller-0/puller-template": createTemplateReference(
            options.accessoriesTemplatesLegacyIds["puller Standard Puller (YKK, n56) with Slider, M5"],
        ),
        "accessories-bottom-curve/accessory-stack/accessory-0/accessory/puller-0/puller-offset": 0.995,
        "accessories-bottom-curve/accessory-stack/accessory-1/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["tape 8mm"]),
        ...tapeForLayerTopCurveAccessories(options, 0),
        ...getDefaultHandlesParameters(options),
    }

    initInstanceTemplateCommon(
        modifyTemplateGraph,
        {
            legacyId: masterTemplateDataLegacyId,
            instanceName: "Edge (Master)",
            instanceId: "edge-master",
        },
        {
            ...options,
            parameters: optionsParameters,
        },
    )
}

export function initEdgeWithExtraLaneMasterTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    options: InitStackableMattressStandardModelMasterTemplateOptions = {openingOnBottom: false},
) {
    return initEdgeMasterTemplate(modifyTemplateGraph, options)
}

export function initEdgeWithExtraLaneInstanceTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    masterTemplateDataLegacyId: number,
    options: InitMattressInstanceTemplateOptions,
    extraLaneFabricMaterialLegacyId: number,
) {
    const optionsParameters = {
        ...(options.parameters ? options.parameters : {}),
        ...defaultDimensions,
        "layer-count": 3,
        "fabric-stack/layer-0/height": 7,
        "fabric-stack/layer-1/height": 6,
        "fabric-stack/layer-1/material": new MaterialReference({
            parameters: {
                name: "Extra Lane Material",
                materialId: extraLaneFabricMaterialLegacyId,
                materialRevisionId: null,
            },
        }),
        "fabric-stack/layer-2/height": 7,
        "accessories-bottom-curve/accessory-count": 1,
        "accessories-bottom-curve/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["tape 8mm"]),
        ...tapeForLayerTopCurveAccessories(options, 2),
        ...getDefaultHandlesParameters(options),
    }

    initInstanceTemplateCommon(
        modifyTemplateGraph,
        {
            legacyId: masterTemplateDataLegacyId,
            instanceName: "Edge with extra lane (Master)",
            instanceId: "edge-with-extra-lane-master",
        },
        {
            ...options,
            parameters: optionsParameters,
        },
    )
}

export function initEdgeTopperLookMasterTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    options: InitStackableMattressStandardModelMasterTemplateOptions = {openingOnBottom: false},
) {
    return initEdgeMasterTemplate(modifyTemplateGraph, options)
}

export function initEdgeTopperLookInstanceTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    masterTemplateDataLegacyId: number,
    options: InitMattressInstanceTemplateOptions,
) {
    const optionsParameters = {
        ...(options.parameters ? options.parameters : {}),
        ...defaultDimensions,
        "layer-count": 2,
        "fabric-stack/layer-0/height": 20,
        "fabric-stack/layer-1/height": 5,
        "accessories-bottom-curve/accessory-count": 1,
        "accessories-bottom-curve/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["tape 8mm"]),
        ...tapeForLayerTopCurveAccessories(options, 0),
        ...tapeForLayerTopCurveAccessories(options, 1),
        ...getDefaultHandlesParameters(options),
    }

    initInstanceTemplateCommon(
        modifyTemplateGraph,
        {
            legacyId: masterTemplateDataLegacyId,
            instanceName: "Edge topper look (Master)",
            instanceId: "edge-topper-look-master",
        },
        {
            ...options,
            parameters: optionsParameters,
        },
    )
}

export function initPillowTopWithZipperMasterTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    options: InitStackableMattressStandardModelMasterTemplateOptions = {openingOnBottom: false},
) {
    return initEdgeMasterTemplate(modifyTemplateGraph, options)
}

export function initPillowTopWithZipperInstanceTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    masterTemplateDataLegacyId: number,
    options: InitMattressInstanceTemplateOptions,
) {
    const optionsParameters = {
        ...(options.parameters ? options.parameters : {}),
        ...defaultDimensions,
        "layer-count": 2,
        "fabric-stack/layer-0/height": 20,
        "fabric-stack/layer-1/height": 5,
        "accessories-bottom-curve/accessory-count": 1,
        "accessories-bottom-curve/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["tape 8mm"]),
        "fabric-stack/layer-0/accessories-layer-top-curve/accessory-count": 3,
        "fabric-stack/layer-0/accessories-layer-top-curve/accessory-stack/accessory-0/accessory": createTemplateReference(
            options.accessoriesTemplatesLegacyIds["piping 5.5mm"],
        ),
        "fabric-stack/layer-0/accessories-layer-top-curve/accessory-stack/accessory-1/accessory": createTemplateReference(
            options.accessoriesTemplatesLegacyIds["zipper Teeth, M5"],
        ),
        "fabric-stack/layer-0/accessories-layer-top-curve/accessory-stack/accessory-1/accessory/puller-0/puller-template": createTemplateReference(
            options.accessoriesTemplatesLegacyIds["puller Standard Puller (YKK, n56) with Slider, M5"],
        ),
        "fabric-stack/layer-0/accessories-layer-top-curve/accessory-stack/accessory-1/accessory/puller-0/puller-offset": 0.995,
        "fabric-stack/layer-0/accessories-layer-top-curve/accessory-stack/accessory-2/accessory": createTemplateReference(
            options.accessoriesTemplatesLegacyIds["piping 5.5mm"],
        ),
        ...tapeForLayerTopCurveAccessories(options, 1),
        ...getDefaultHandlesParameters(options),
    }

    initInstanceTemplateCommon(
        modifyTemplateGraph,
        {
            legacyId: masterTemplateDataLegacyId,
            instanceName: "Pillow top with zipper (Master)",
            instanceId: "pillow-top-with-zipper-master",
        },
        {
            ...options,
            parameters: optionsParameters,
        },
    )
}
